import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import GlobalContext from 'contexts/GlobalContext';
import { ViewManagerContext } from 'contexts/ViewManagerContext';
import { MultiRunIntroConfig, useFeatureFlag, useFeatureFlagsReady } from 'featureFlags';
import { useAppDispatch, useAppSelector } from 'hooks';
import { isBiometricEnrollmentOfferPending } from 'hooks/useBiometricEnrollmentOffer';
import { setFeatureIntroModalOpen, setFeatureIntroSlot } from 'reducers/playerPromos';

import { adaptActiveTables } from 'components/SaloonLobby/useActiveTables';

import { reportMultiRunIntroEvent } from './multiRunIntroAnalytics';
import { resolveMultiRunIntroContent } from './multiRunIntroCopy';
import {
  canOpenIntro,
  decideIntroSlot,
  isInLivePlay,
  mustTearDownIntro
} from './multiRunIntroGate';
import MultiRunIntroModal, { MultiRunIntroDismissReason } from './MultiRunIntroModal';
import { followMultiRunIntroDismissal } from './multiRunIntroNavigation';
import { markMultiRunIntroSeen, readMultiRunIntroSeen } from './multiRunIntroSeen';

/**
 * Presents the one-time multi-run introduction (EN-10791): the first time a
 * player logs in with the feature launched, once, and never again after they
 * dismiss it.
 *
 * Same design and presentation as the promo entry modal; different logic
 * (owner, 2026-09-24):
 *   - the trigger is the `release.multi-run-intro` flag, not a live promo. Its
 *     value is an EDITION (nlhe-launch, omaha-live, …): the copy to show, and
 *     the seen key it is shown once under;
 *   - "seen" is written when the player dismisses it, not opt-in by checkbox;
 *   - it and the promo share ONE login-modal slot per session, and are never
 *     both presented. When this takes the session the promo sits it out and
 *     returns on the next login, as it already does every login.
 *
 * The slot is decided as soon as the player's flags are known
 * (useFeatureFlagsReady — bounded, so the promo is never held for long) and
 * held for the session: the modal then waits for the first moment it may open
 * — the lobby, loaded, a valid location, after any biometric enrollment offer,
 * never over live play at another table — which can be later in the session.
 *
 * The edition is PINNED when the slot is decided (Keith, #4004): the player
 * sees exactly that edition and it is exactly that edition that is marked
 * seen, even if the flag's variation changes while they wait or read. Only a
 * switch to off ends it early. The next session picks up the new edition —
 * the CLAUDE.md rule to apply flag changes only at a safe boundary.
 */
type MultiRunIntroAutoPromptProps = {
  /**
   * The biometric enrollment sheet (or its confirmation beat) is on screen.
   * App.tsx owns that offer's state; the introduction never stacks on it.
   */
  biometricPromptOpen?: boolean;
};

type FinishOutcome = 'dismissed' | 'interrupted' | 'released';

const MultiRunIntroAutoPrompt = ({ biometricPromptOpen = false }: MultiRunIntroAutoPromptProps) => {
  const dispatch = useAppDispatch();
  const playerGUID = useAppSelector((state) => state.user.playerGUID);
  const guest = useAppSelector((state) => state.user.guest);
  const slot = useAppSelector((state) => state.playerPromos.featureIntroSlot);
  const flagsReady = useFeatureFlagsReady();
  const config = useFeatureFlag('multiRunIntro');
  const identity = !guest && playerGUID ? playerGUID : null;

  // Who this session's slot was decided for, and the edition it was decided
  // on (null when the introduction does not take the session); and the player
  // whose attempt this session is over (dismissed, torn down, or released).
  const [decided, setDecided] = useState<{
    identity: string;
    config: MultiRunIntroConfig | null;
  } | null>(null);
  const [finishedFor, setFinishedFor] = useState<string | null>(null);

  // Decide the slot once per player per session, the moment the player is
  // known. Login sets playerGUID before the promos are even requested (App.tsx,
  // GOT_LOGIN), so by the time the promo has anything to show it already reads
  // 'pending' or the decision. Deciding in a layout effect puts that in the
  // store before the next paint. If the promo ever did open first, the
  // introduction still waits for it to close (canOpenIntro).
  useLayoutEffect(() => {
    if (!identity) {
      // Logging out ends the session: the next login is a fresh attempt, so
      // an introduction that was interrupted this session is offered again.
      if (decided !== null) setDecided(null);
      if (finishedFor !== null) setFinishedFor(null);
      if (slot !== 'none') dispatch(setFeatureIntroSlot('none'));
      return;
    }
    if (decided?.identity === identity) return;

    const next = decideIntroSlot({
      identity,
      flagsReady,
      flagOn: config !== null,
      seen: config ? readMultiRunIntroSeen(config.edition, identity) : 'unseen'
    });
    if (next !== slot) dispatch(setFeatureIntroSlot(next));
    // 'pending' is re-decided when the flags land; anything else is final
    // for the session (a flag flipped on mid-session waits for the next one).
    // A reservation pins the edition it was decided on.
    if (next !== 'pending') setDecided({ identity, config: next === 'reserved' ? config : null });
  }, [identity, flagsReady, config, slot, decided, finishedFor, dispatch]);

  const handleFinished = useCallback(
    (outcome: FinishOutcome) => {
      setFinishedFor(identity);
      // The flag went off before the introduction ever showed: hand the
      // session back so the promo may have it.
      if (outcome === 'released') dispatch(setFeatureIntroSlot('none'));
    },
    [identity, dispatch]
  );

  const pinned = decided?.identity === identity ? decided.config : null;
  const contending =
    identity !== null && pinned !== null && slot === 'reserved' && finishedFor !== identity;
  if (!contending) return null;

  return (
    <MultiRunIntroPresenter
      identity={identity}
      config={pinned}
      flagOn={config !== null}
      biometricPromptOpen={biometricPromptOpen}
      onFinished={handleFinished}
    />
  );
};

// The storage-backed half of the biometric gate does not notify, so it is
// re-read on this clock while it is the only thing holding the modal back.
const BIOMETRIC_RECHECK_MS = 1000;

// How long a stored biometric offer may hold the introduction back. The offer
// resolves once the profile — and the email its credential is keyed by —
// loads, normally seconds after login. If the profile never loads, the stored
// flag survives to a later launch (useBiometricEnrollmentOffer), and without a
// ceiling it would hold the introduction, and with it the promo the reserved
// slot keeps out, for the rest of the session.
export const BIOMETRIC_OFFER_WAIT_MS = 15000;

type MultiRunIntroPresenterProps = {
  identity: string;
  /** The edition pinned when the slot was decided: what shows, and what is marked seen. */
  config: MultiRunIntroConfig;
  /** The flag right now. Only a switch to off ends an attempt early. */
  flagOn: boolean;
  biometricPromptOpen: boolean;
  onFinished: (outcome: FinishOutcome) => void;
};

/**
 * Mounted only while the introduction holds the slot and has not finished, so
 * the table subscription it needs for the live-play gate costs nothing for the
 * rest of a session (or for every session after the player has seen it).
 */
const MultiRunIntroPresenter = ({
  identity,
  config,
  flagOn,
  biometricPromptOpen,
  onFinished
}: MultiRunIntroPresenterProps) => {
  const dispatch = useAppDispatch();
  const isLobbyReady = useAppSelector((state) => state.lobby.isLobbyReady);
  const promoEntryModalOpen = useAppSelector((state) => state.playerPromos.promoEntryModalOpen);
  const playerGames = useAppSelector((state) => state.playerGames);
  const tables = useAppSelector((state) => (state as any).tables) as Record<string, any>;
  const { activeView } = useContext(ViewManagerContext);
  const { isLocationInvalid } = useContext(GlobalContext);
  const [open, setOpen] = useState(false);
  const [, setRecheck] = useState(0);
  const presentedFromRef = useRef(Date.now());
  const content = useMemo(() => resolveMultiRunIntroContent(config), [config]);
  // Report the outcome exactly once, and only give the session back to the
  // promo if the introduction never reached the screen.
  const shownRef = useRef(false);
  const finishedRef = useRef(false);
  const finish = useCallback(
    (outcome: FinishOutcome) => {
      if (finishedRef.current) return;
      finishedRef.current = true;
      onFinished(outcome);
    },
    [onFinished]
  );

  // Same derivation the dock uses (useActiveTables), without that hook's
  // ordering side effects.
  const inLivePlay = useMemo(
    () => isInLivePlay(adaptActiveTables(playerGames || [], tables || {})),
    [playerGames, tables]
  );
  const biometricOfferPending =
    isBiometricEnrollmentOfferPending() &&
    Date.now() - presentedFromRef.current < BIOMETRIC_OFFER_WAIT_MS;
  const biometricPromptActive = biometricPromptOpen || biometricOfferPending;
  const gatesPass =
    flagOn &&
    canOpenIntro({
      activeView,
      isLobbyReady: Boolean(isLobbyReady),
      isLocationInvalid,
      biometricPromptActive,
      inLivePlay,
      promoEntryModalOpen: Boolean(promoEntryModalOpen)
    });

  // The flag went off before it showed.
  useEffect(() => {
    if (!shownRef.current && !flagOn) finish('released');
  }, [flagOn, finish]);

  useEffect(() => {
    if (open || shownRef.current || !gatesPass) return;
    shownRef.current = true;
    setOpen(true);
    reportMultiRunIntroEvent({ name: 'multi_run_intro_shown', edition: config.edition }, identity);
  }, [open, gatesPass, config.edition, identity]);

  // Close WITHOUT counting as seen if live play, the location, the view, the
  // flag or the biometric sheet changes underneath it. That ends this
  // session's attempt (no flicker back open); the player gets it on the next
  // session. It is still reported, once, as a dismissal whose reason is
  // 'interrupted', so a showing never goes missing from the data (Cory).
  useEffect(() => {
    if (!open) return;
    if (
      mustTearDownIntro({ activeView, isLocationInvalid, inLivePlay, flagOn, biometricPromptOpen })
    ) {
      setOpen(false);
      if (!finishedRef.current) {
        reportMultiRunIntroEvent(
          {
            name: 'multi_run_intro_dismissed',
            edition: config.edition,
            reason: 'interrupted'
          },
          identity
        );
      }
      finish('interrupted');
    }
  }, [
    identity,
    open,
    activeView,
    isLocationInvalid,
    inLivePlay,
    flagOn,
    biometricPromptOpen,
    config.edition,
    finish
  ]);

  useEffect(() => {
    if (open || biometricPromptOpen || !biometricOfferPending) return undefined;
    const timer = window.setInterval(() => setRecheck((n) => n + 1), BIOMETRIC_RECHECK_MS);
    return () => window.clearInterval(timer);
  }, [open, biometricPromptOpen, biometricOfferPending]);

  // The ticker holds its rotation while this is open, as it does for the promo.
  useEffect(() => {
    dispatch(setFeatureIntroModalOpen(open));
  }, [dispatch, open]);
  useEffect(
    () => () => {
      dispatch(setFeatureIntroModalOpen(false));
    },
    [dispatch]
  );

  const handleDismiss = useCallback(
    (reason: MultiRunIntroDismissReason) => {
      // Best effort: if storage refuses the write, finishing still ends this
      // session's attempt, so it cannot repeat until the next login.
      markMultiRunIntroSeen(config.edition, identity);
      reportMultiRunIntroEvent(
        {
          name: 'multi_run_intro_dismissed',
          edition: config.edition,
          reason
        },
        identity
      );
      setOpen(false);
      finish('dismissed');
      followMultiRunIntroDismissal(reason);
    },
    [identity, config.edition, finish]
  );

  if (!open) return null;

  return <MultiRunIntroModal onDismiss={handleDismiss} content={content} />;
};

export default MultiRunIntroAutoPrompt;
